"use client";

import Link from "next/link";
import { useEffect, useRef, useState, useCallback } from "react";
import { useRouter } from "next/navigation";
import {
  MapPin,
  Search,
  Loader2,
  LocateFixed,
  XCircle,
  ArrowLeft,
  Utensils,
  Store,
  AlertCircle // <-- Yeh add kar diya gaya hai
} from "lucide-react";

type LoggedInUser = {
  id: number;
  name: string;
  mobile: string;
  address?: string | null;
  latitude?: number | null;
  longitude?: number | null;
};

type Food = {
  id: number;
  foodName: string;
  quantity: number;
  mealType: string;
  availableOn: string;
  price: number;
  location: string;
  latitude?: number | null;
  longitude?: number | null;
  image?: string | null;
  status?: string;
  distanceKm?: number | null;
  seller?: {
    id: number;
    name: string;
    foodSellerName?: string | null;
    foodSellerType?: string | null;
  } | null;
};

type RestaurantMenuItem = {
  id: number;
  name: string;
  halfPlatePrice?: number | null;
  fullPlatePrice?: number | null;
  numberPrice?: number | null;
  image?: string | null;
  sellerId: number;
  distanceKm?: number | null;
  seller?: {
    id: number;
    foodSellerName?: string | null;
    foodSellerType?: string | null;
    foodSellerAvailable?: boolean;
    address?: string | null;
    latitude?: number | null;
    longitude?: number | null;
  } | null;
};

type DistanceFilter = 2 | 5 | 25 | 50;
type FoodSellerFilter = "All" | "HomeCook" | "Restaurant";

// ============================================================================
// NEARBY FOOD MAP COMPONENT
// ============================================================================

type NearbyFoodMapProps = {
  foods: Food[];
  restaurants: RestaurantMenuItem[];
  buyerLatitude: number | null;
  buyerLongitude: number | null;
  radiusKm: DistanceFilter;
  onLocationChange: (latitude: number, longitude: number) => void;
};

function NearbyFoodMap({
  foods,
  restaurants,
  buyerLatitude,
  buyerLongitude,
  radiusKm,
  onLocationChange,
}: NearbyFoodMapProps) {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<any>(null);
  const locationChangeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mapViewRef = useRef<{ center: [number, number]; zoom: number; } | null>(null);
  const routeRequestIdRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    if (!mapContainerRef.current) return;

    async function createMap() {
      try {
        if (typeof document !== "undefined") {
          let link = document.querySelector('link[data-apnasaathi-leaflet="true"]') as HTMLLinkElement | null;
          if (!link) {
            link = document.createElement("link");
            link.rel = "stylesheet";
            link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
            link.dataset.apnasaathiLeaflet = "true";
            document.head.appendChild(link);
          }
          if (!link.sheet) {
            await new Promise<void>((resolve) => {
              let finished = false;
              const finish = () => { if (finished) return; finished = true; resolve(); };
              link?.addEventListener("load", finish, { once: true });
              link?.addEventListener("error", finish, { once: true });
              setTimeout(finish, 1500);
            });
          }
        }

        const leafletModule = await import("leaflet");
        if (cancelled || !mapContainerRef.current || !mapContainerRef.current.isConnected) return;

        const L = leafletModule.default ?? leafletModule;

        if (mapInstanceRef.current) {
          try { mapInstanceRef.current.remove(); } catch {}
          mapInstanceRef.current = null;
        }

        const hasBuyerLocation = buyerLatitude != null && buyerLongitude != null && Number.isFinite(Number(buyerLatitude)) && Number.isFinite(Number(buyerLongitude));
        const buyerPoint: [number, number] | null = hasBuyerLocation ? [Number(buyerLatitude), Number(buyerLongitude)] : null;
        const firstFoodPoint = foods.find((food) => food.latitude != null && food.longitude != null && Number.isFinite(Number(food.latitude)) && Number.isFinite(Number(food.longitude)) && !(Number(food.latitude) === 0 && Number(food.longitude) === 0));
        const initialPoint: [number, number] = buyerPoint ?? (firstFoodPoint ? [Number(firstFoodPoint.latitude), Number(firstFoodPoint.longitude)] : [20.2961, 85.8245]);
        const savedView = mapViewRef.current;
        const defaultZoom = buyerPoint ? radiusKm <= 2 ? 14 : radiusKm <= 5 ? 13 : radiusKm <= 25 ? 11 : 10 : firstFoodPoint ? 11 : 5;

        const container = mapContainerRef.current;
        if (!container || !container.isConnected) return;

        const map = L.map(container, { preferCanvas: true, scrollWheelZoom: false }).setView(savedView?.center ?? initialPoint, savedView?.zoom ?? defaultZoom);
        mapInstanceRef.current = map;

        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19 }).addTo(map);

        const makeMarkerIcon = (label: string, background: string) => L.divIcon({
          className: "",
          html: `<div style="min-width:38px;height:38px;padding:0 8px;border-radius:19px;background:${background};color:white;border:3px solid white;box-shadow:0 2px 9px rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center;font-weight:900;font-size:15px;white-space:nowrap;">${label}</div>`,
          iconSize: [46, 38], iconAnchor: [23, 19], popupAnchor: [0, -20],
        });

        const boundsPoints: [number, number][] = [];

        if (buyerPoint) {
          const buyerMarker = L.marker(buyerPoint, { icon: makeMarkerIcon("✚", "#f97316"), draggable: true }).addTo(map).bindPopup("<strong>📍 Your search location</strong><br/>Drag this orange crosshair to change the location, or tap another point on the map.");
          let radiusCircle: any = null;
          radiusCircle = L.circle(buyerPoint, { radius: Number(radiusKm) * 1000, weight: 2, fillOpacity: 0.05, color: "#f97316" }).addTo(map);

          const moveSearchLocation = (latitude: number, longitude: number, markerAlreadyMoved = false) => {
            if (cancelled || mapInstanceRef.current !== map) return;
            const point: [number, number] = [latitude, longitude];
            if (!markerAlreadyMoved) buyerMarker.setLatLng(point);
            if (radiusCircle) radiusCircle.setLatLng(point);
            
            const currentCenter = map.getCenter();
            mapViewRef.current = { center: [Number(currentCenter.lat), Number(currentCenter.lng)], zoom: map.getZoom() };
            
            if (locationChangeTimerRef.current) clearTimeout(locationChangeTimerRef.current);
            locationChangeTimerRef.current = setTimeout(() => {
              if (cancelled || mapInstanceRef.current !== map) return;
              onLocationChange(latitude, longitude);
            }, 200);
          };

          buyerMarker.on("dragend", (event: any) => {
            if (cancelled || mapInstanceRef.current !== map) return;
            const position = event.target.getLatLng();
            moveSearchLocation(Number(position.lat), Number(position.lng), true);
          });

          map.on("click", (event: any) => {
            if (cancelled || mapInstanceRef.current !== map) return;
            moveSearchLocation(Number(event.latlng.lat), Number(event.latlng.lng));
          });
          boundsPoints.push(buyerPoint);
        }

        let activeRouteLayer: any = null;

        async function showRoadRoute(food: Food, foodPoint: [number, number], marker: any, safeFoodName: string, safeSeller: string) {
          const currentRouteRequest = ++routeRequestIdRef.current;
          if (cancelled || mapInstanceRef.current !== map) return;

          if (!buyerPoint) {
            if (cancelled || mapInstanceRef.current !== map) return;
            marker.setPopupContent(`<strong>${safeFoodName}</strong><br/>👨‍🍳 ${safeSeller}<br/>💰 ₹${Number(food.price).toFixed(0)} per plate<br/>📦 ${food.quantity} available<br/><br/><strong>📍 Road route needs your location.</strong><br/>Use “My Current Location” above, then tap this food again.<br/><br/><a href="/food/${food.id}" style="font-weight:800;color:#ea580c;">View & Order →</a>`).openPopup();
            return;
          }

          marker.setPopupContent(`<strong>${safeFoodName}</strong><br/>👨‍🍳 ${safeSeller}<br/>💰 ₹${Number(food.price).toFixed(0)} per plate<br/>📦 ${food.quantity} available<br/><br/>🛣️ Finding road route...`).openPopup();

          try {
            const routeUrl = `https://router.project-osrm.org/route/v1/driving/${buyerPoint[1]},${buyerPoint[0]};${foodPoint[1]},${foodPoint[0]}?overview=full&geometries=geojson&steps=false`;
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 10000);
            let response: Response;
            try { response = await fetch(routeUrl, { signal: controller.signal, headers: { Accept: "application/json" } }); } finally { clearTimeout(timeoutId); }
            if (!response.ok) throw new Error("Route service unavailable");
            
            const data = await response.json();
            if (cancelled || mapInstanceRef.current !== map || currentRouteRequest !== routeRequestIdRef.current) return;
            
            const route = data?.routes?.[0];
            if (!route || !route.geometry || !Array.isArray(route.geometry.coordinates)) throw new Error("Road route not found");

            const routePoints: [number, number][] = route.geometry.coordinates.map((coordinate: [number, number]) => [Number(coordinate[1]), Number(coordinate[0])]).filter((point: [number, number]) => Number.isFinite(point[0]) && Number.isFinite(point[1]));
            if (routePoints.length < 2) throw new Error("Invalid road route");
            if (cancelled || mapInstanceRef.current !== map || !mapContainerRef.current || !mapContainerRef.current.isConnected) return;

            if (activeRouteLayer) {
              try { if (map.hasLayer(activeRouteLayer)) map.removeLayer(activeRouteLayer); } catch {}
              activeRouteLayer = null;
            }

            activeRouteLayer = L.polyline(routePoints, { weight: 6, opacity: 0.8, color: "#ea580c" });
            if (cancelled || mapInstanceRef.current !== map || !mapContainerRef.current || !mapContainerRef.current.isConnected) { activeRouteLayer = null; return; }
            try { activeRouteLayer.addTo(map); } catch { activeRouteLayer = null; return; }

            const routeBounds = activeRouteLayer.getBounds();
            if (!cancelled && mapInstanceRef.current === map && routeBounds.isValid()) {
              map.fitBounds(routeBounds, { padding: [45, 45], maxZoom: 15 });
            }

            const roadDistanceKm = Number(route.distance || 0) / 1000;
            const roadDurationMinutes = Math.max(1, Math.round(Number(route.duration || 0) / 60));
            if (cancelled || mapInstanceRef.current !== map || !mapContainerRef.current || !mapContainerRef.current.isConnected) return;

            marker.setPopupContent(`<strong>${safeFoodName}</strong><br/>👨‍🍳 ${safeSeller}<br/>💰 ₹${Number(food.price).toFixed(0)} per plate<br/>📦 ${food.quantity} available<br/><br/><strong>🛣️ Road Route</strong><br/>📏 Distance: ${roadDistanceKm.toFixed(1)} km<br/>⏱️ Approx. driving time: ${roadDurationMinutes} min<br/><br/><a href="/food/${food.id}" style="font-weight:800;color:#ea580c;">View & Order →</a>`).openPopup();
          } catch {
            if (cancelled || mapInstanceRef.current !== map || !mapContainerRef.current || !mapContainerRef.current.isConnected || currentRouteRequest !== routeRequestIdRef.current) return;
            const fallbackDistance = food.distanceKm != null ? `${Number(food.distanceKm).toFixed(1)} km` : "Unavailable";
            marker.setPopupContent(`<strong>${safeFoodName}</strong><br/>👨‍🍳 ${safeSeller}<br/>💰 ₹${Number(food.price).toFixed(0)} per plate<br/>📦 ${food.quantity} available<br/><br/>📏 Nearby distance: ${fallbackDistance}<br/>⚠️ Road route is temporarily unavailable.<br/>You can still view and order this food.<br/><br/><a href="/food/${food.id}" style="font-weight:800;color:#ea580c;">View & Order →</a>`).openPopup();
          }
        }

        foods.forEach((food) => {
          if (food.latitude == null || food.longitude == null || !Number.isFinite(Number(food.latitude)) || !Number.isFinite(Number(food.longitude)) || (Number(food.latitude) === 0 && Number(food.longitude) === 0)) return;
          const foodPoint: [number, number] = [Number(food.latitude), Number(food.longitude)];
          boundsPoints.push(foodPoint);

          const safeFoodName = String(food.foodName).replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll('"', "&quot;").replaceAll("'", "&#039;");
          const safeSeller = String(food.seller?.name || "Home Chef").replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll('"', "&quot;").replaceAll("'", "&#039;");
          const distanceText = food.distanceKm != null ? `${Number(food.distanceKm).toFixed(1)} km away` : "Tap to calculate road distance";

          const marker = L.marker(foodPoint, { icon: makeMarkerIcon("🍱", "#16a34a") }).addTo(map);
          marker.bindPopup(`<strong>${safeFoodName}</strong><br/>👨‍🍳 ${safeSeller}<br/>💰 ₹${Number(food.price).toFixed(0)} per plate<br/>📦 ${food.quantity} available<br/>📍 ${distanceText}<br/><br/><strong>Tap this marker to show the road route.</strong><br/><br/><a href="/food/${food.id}" style="font-weight:800;color:#ea580c;">View & Order →</a>`);
          marker.on("click", () => { void showRoadRoute(food, foodPoint, marker, safeFoodName, safeSeller); });
        });

        // Show one marker per approved, online restaurant.
        restaurants.forEach((restaurant) => {
          const lat = Number(restaurant.seller?.latitude);
          const lng = Number(restaurant.seller?.longitude);

          if (!Number.isFinite(lat) || !Number.isFinite(lng) ||
              (lat === 0 && lng === 0) ||
              restaurant.seller?.latitude == null ||
              restaurant.seller?.longitude == null) return;

          const point: [number, number] = [lat, lng];
          boundsPoints.push(point);

          const restaurantName = String(
            restaurant.seller?.foodSellerName || "Restaurant"
          ).replaceAll("&", "&amp;")
           .replaceAll("<", "&lt;")
           .replaceAll(">", "&gt;")
           .replaceAll('"', "&quot;")
           .replaceAll("'", "&#039;");

          const marker = L.marker(point, {
            icon: makeMarkerIcon("🏪", "#2563eb"),
          }).addTo(map);

          marker.bindPopup(
            `<strong>🏪 ${restaurantName}</strong>` +
            `<br/>🍽️ Restaurant Menu` +
            `<br/><br/><a href="/restaurant/${restaurant.sellerId}" ` +
            `style="font-weight:800;color:#ea580c;">View Menu →</a>`
          );
        });

        if (!buyerPoint) {
          if (boundsPoints.length > 1) { map.fitBounds(L.latLngBounds(boundsPoints), { padding: [35, 35], maxZoom: 14 }); } 
          else if (boundsPoints.length === 1) { map.setView(boundsPoints[0], 13); }
        }

        setTimeout(() => {
          if (cancelled || mapInstanceRef.current !== map || !mapContainerRef.current || !mapContainerRef.current.isConnected) return;
          try { map.invalidateSize({ pan: false }); } catch {}
        }, 150);
      } catch (error) {
        if (!cancelled) console.error("Unable to show nearby food map:", error);
      }
    }

    void createMap();
    return () => {
      cancelled = true;
      routeRequestIdRef.current += 1;
      if (locationChangeTimerRef.current) { clearTimeout(locationChangeTimerRef.current); locationChangeTimerRef.current = null; }
      if (mapInstanceRef.current) { try { mapInstanceRef.current.remove(); } catch {} mapInstanceRef.current = null; }
    };
  }, [foods, restaurants, buyerLatitude, buyerLongitude, radiusKm, onLocationChange]);

  const hasBuyerLocation = buyerLatitude != null && buyerLongitude != null;

  return (
    <div className="bg-white rounded-[2rem] shadow-sm border border-slate-200 p-4 md:p-6 mb-8 mt-5 relative z-0">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
        <div>
          <h2 className="text-xl font-black text-slate-900 flex items-center gap-2">🗺️ Food Map</h2>
          <p className="text-xs font-semibold text-slate-500 mt-1">
            {hasBuyerLocation ? `Drag the orange ✚ to change location. Showing food within ${radiusKm} km.` : "Tap a food marker to see details and route."}
          </p>
        </div>
        <span className="bg-orange-100 text-orange-800 px-4 py-1.5 rounded-full text-xs font-black border border-orange-200 w-fit">
          🍱 {foods.length} offer{foods.length === 1 ? "" : "s"}
        </span>
      </div>
      <div ref={mapContainerRef} className="w-full h-[380px] md:h-[480px] rounded-2xl overflow-hidden border border-slate-200 bg-slate-100 relative z-0" aria-label="Nearby food offers map" />
    </div>
  );
}

// ============================================================================
// MAIN PAGE COMPONENT
// ============================================================================

export default function BuyFoodPage() {
  const router = useRouter();
  const mapLocationNameRequestRef = useRef(0);

  const [foods, setFoods] = useState<Food[]>([]);
  const [restaurantItems, setRestaurantItems] = useState<RestaurantMenuItem[]>([]);
  const [search, setSearch] = useState("");
  const [foodSellerFilter, setFoodSellerFilter] = useState<FoodSellerFilter>("All");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [user, setUser] = useState<LoggedInUser | null>(null);

  const [searchLatitude, setSearchLatitude] = useState<number | null>(null);
  const [searchLongitude, setSearchLongitude] = useState<number | null>(null);
  const [distanceFilter, setDistanceFilter] = useState<DistanceFilter>(5);

  const [gettingLocation, setGettingLocation] = useState(false);
  const [manualLocation, setManualLocation] = useState("");
  const [searchingLocation, setSearchingLocation] = useState(false);
  const [selectedLocationLabel, setSelectedLocationLabel] = useState("");
  const [locationReady, setLocationReady] = useState(false);

  useEffect(() => { void loadCurrentUser(); }, []);

  useEffect(() => {
    if (!locationReady) return;
    void loadFoods();
  }, [locationReady, searchLatitude, searchLongitude, distanceFilter]);

  async function loadCurrentUser() {
    try {
      setError("");
      const response = await fetch("/api/me", { cache: "no-store" });
      if (!response.ok) { alert("Please login to see food near you."); router.push("/login"); return; }
      
      const data = (await response.json()) as LoggedInUser;
      setUser(data);
      setSearchLatitude(data.latitude ?? null);
      setSearchLongitude(data.longitude ?? null);

      if (data.latitude != null && data.longitude != null) {
        setSelectedLocationLabel(data.address?.trim() || "Saved profile location");
        setManualLocation(data.address?.trim() || "Saved profile location");
      }
      setLocationReady(true);
    } catch (error) {
      console.error("Unable to verify session:", error);
      setError("Unable to verify your login.");
      setLocationReady(true);
    }
  }

  function calculateDistanceKm(latitude1: number, longitude1: number, latitude2: number, longitude2: number) {
    const earthRadiusKm = 6371;
    const toRadians = (value: number) => (value * Math.PI) / 180;
    const latitudeDifference = toRadians(latitude2 - latitude1);
    const longitudeDifference = toRadians(longitude2 - longitude1);
    const a = Math.sin(latitudeDifference / 2) ** 2 + Math.cos(toRadians(latitude1)) * Math.cos(toRadians(latitude2)) * Math.sin(longitudeDifference / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return earthRadiusKm * c;
  }

  async function loadFoods() {
    try {
      setLoading(true); setError("");
      const params = new URLSearchParams();
      if (searchLatitude !== null && searchLongitude !== null) {
        params.set("latitude", String(searchLatitude)); params.set("longitude", String(searchLongitude));
      }
      params.set("radiusKm", String(distanceFilter));
      const query = params.toString();

      const [foodResponse, restaurantResponse] = await Promise.all([
        fetch(query ? `/api/food?${query}` : "/api/food", { cache: "no-store" }),
        fetch("/api/restaurant-menu?mode=public", { cache: "no-store" }),
      ]);

      const foodData = await foodResponse.json();
      const restaurantData = await restaurantResponse.json();

      if (!foodResponse.ok) { if (foodResponse.status === 401) { router.push("/login"); return; } throw new Error(foodData.message || "Unable to load food"); }
      if (!restaurantResponse.ok) { throw new Error(restaurantData.message || "Unable to load restaurant food"); }

      setFoods(Array.isArray(foodData) ? foodData : []);

      const publicRestaurantItems: RestaurantMenuItem[] = Array.isArray(restaurantData) ? restaurantData : [];
      const nearbyRestaurantItems = publicRestaurantItems.map((item) => {
          const latitude = item.seller?.latitude;
          const longitude = item.seller?.longitude;
          if (searchLatitude === null || searchLongitude === null || latitude == null || longitude == null || !Number.isFinite(Number(latitude)) || !Number.isFinite(Number(longitude))) {
            return { ...item, distanceKm: null };
          }
          return { ...item, distanceKm: calculateDistanceKm(searchLatitude, searchLongitude, Number(latitude), Number(longitude)) };
        })
        .filter((item) => searchLatitude === null || searchLongitude === null || (item.distanceKm !== null && item.distanceKm <= distanceFilter))
        .sort((first, second) => (first.distanceKm ?? Number.MAX_SAFE_INTEGER) - (second.distanceKm ?? Number.MAX_SAFE_INTEGER));

      setRestaurantItems(nearbyRestaurantItems);
    } catch (error) {
      console.error("Load nearby food error:", error);
      setError("Unable to load food offers.");
      setFoods([]); setRestaurantItems([]);
    } finally {
      setLoading(false);
    }
  }

  // EXACT GPS LOGIC WITH 30s TIMEOUT & FALLBACK
  async function getBrowserLocation() {
    setGettingLocation(true);
    try {
      if (!navigator.geolocation) {
        alert("Geolocation is not supported by this browser.");
        return;
      }

      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 30000,
          maximumAge: 0,
        });
      });

      const lat = Number(position.coords.latitude.toFixed(6));
      const lng = Number(position.coords.longitude.toFixed(6));
      
      setSearchLatitude(lat);
      setSearchLongitude(lng);
      
      const fallbackText = `My Location (${lat}, ${lng})`;
      setManualLocation(fallbackText);
      setSelectedLocationLabel(fallbackText);

      void updateSearchLocationFromMap(lat, lng);

    } catch (err: any) {
      console.error("GPS error details:", err);
      let errorMsg = "Could not fetch your current GPS location.";
      if (err?.code === 1) errorMsg = "Please allow Location permission for this website, then try again.";
      else if (err?.code === 2) errorMsg = "Location is unavailable. Turn on your phone's Location/GPS and try again.";
      else if (err?.code === 3) errorMsg = "GPS took too long. Move near a window/outdoors and try Current GPS again.";
      
      // Fallback for timeout error
      if (err?.code === 3) {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            const lat = Number(pos.coords.latitude.toFixed(6));
            const lng = Number(pos.coords.longitude.toFixed(6));
            setSearchLatitude(lat); setSearchLongitude(lng);
            setManualLocation(`My Location (${lat}, ${lng})`);
            void updateSearchLocationFromMap(lat, lng);
          },
          () => alert(errorMsg),
          { enableHighAccuracy: false, timeout: 30000, maximumAge: 60000 }
        );
      } else {
        alert(errorMsg);
      }
    } finally {
      setGettingLocation(false);
    }
  }

  async function searchManualLocation() {
    const value = manualLocation.trim();
    if (!value) { alert("Type an area, landmark, city or address first."); return; }
    try {
      setSearchingLocation(true);
      const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=1&countrycodes=in&q=${encodeURIComponent(value)}`;
      const controller = new AbortController();
      const timeoutId = setTimeout(() => { controller.abort(); }, 10000);
      let response: Response;
      try { response = await fetch(url, { signal: controller.signal, headers: { Accept: "application/json" } }); } finally { clearTimeout(timeoutId); }
      if (!response.ok) throw new Error("Location search failed");
      const results = await response.json();
      const first = Array.isArray(results) ? results[0] : null;

      if (!first || !Number.isFinite(Number(first.lat)) || !Number.isFinite(Number(first.lon))) {
        alert("Location not found. Try a nearby landmark, area, city or PIN code."); return;
      }
      setSearchLatitude(Number(first.lat)); setSearchLongitude(Number(first.lon));
      const foundLabel = String(first.display_name || value);
      setSelectedLocationLabel(foundLabel); setManualLocation(foundLabel);
    } catch {
      alert("Unable to search that location right now. You can use the map or My Current Location instead.");
    } finally {
      setSearchingLocation(false);
    }
  }

  const updateSearchLocationFromMap = useCallback(async (latitude: number, longitude: number) => {
    const requestId = ++mapLocationNameRequestRef.current;
    setSearchLatitude(latitude); setSearchLongitude(longitude);
    const coordinateLabel = `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`;
    setSelectedLocationLabel(coordinateLabel); setManualLocation(coordinateLabel);

    try {
      const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${encodeURIComponent(String(latitude))}&lon=${encodeURIComponent(String(longitude))}`;
      const controller = new AbortController();
      const timeoutId = setTimeout(() => { controller.abort(); }, 8000);
      let response: Response;
      try { response = await fetch(url, { signal: controller.signal, headers: { Accept: "application/json" } }); } finally { clearTimeout(timeoutId); }
      if (!response.ok) return;
      const data = await response.json();
      if (requestId !== mapLocationNameRequestRef.current) return;
      const label = String(data?.display_name || coordinateLabel).trim();
      setSelectedLocationLabel(label); setManualLocation(label);
    } catch {}
  }, []);

  const visibleFoods = foods.filter((food) => {
    const matchesSearch = food.foodName.toLowerCase().includes(search.trim().toLowerCase());
    const isAvailable = food.status !== "Sold Out" && food.quantity > 0;
    return matchesSearch && isAvailable && foodSellerFilter !== "Restaurant";
  });

  const visibleRestaurantItems = restaurantItems.filter((item) => {
    if (foodSellerFilter === "HomeCook") return false;
    const searchValue = search.trim().toLowerCase();
    if (!searchValue) return true;
    const restaurantName = item.seller?.foodSellerName || "";
    return item.name.toLowerCase().includes(searchValue) || restaurantName.toLowerCase().includes(searchValue);
  });

  const visibleRestaurants = Array.from(
    visibleRestaurantItems.reduce((restaurants, item) => {
      const existing = restaurants.get(item.sellerId);
      if (!existing) { restaurants.set(item.sellerId, item); return restaurants; }
      const existingDistance = existing.distanceKm ?? Number.MAX_SAFE_INTEGER;
      const itemDistance = item.distanceKm ?? Number.MAX_SAFE_INTEGER;
      if (itemDistance < existingDistance) restaurants.set(item.sellerId, item);
      return restaurants;
    }, new Map<number, RestaurantMenuItem>()).values()
  ).sort((first, second) => (first.distanceKm ?? Number.MAX_SAFE_INTEGER) - (second.distanceKm ?? Number.MAX_SAFE_INTEGER));

  return (
    <main className="min-h-screen bg-slate-50 p-4 md:p-8 font-sans text-slate-900 selection:bg-orange-500 selection:text-white pb-24">
      <div className="max-w-5xl mx-auto space-y-8">
        
        {/* HEADER */}
        <div className="flex flex-col gap-4 border-b border-slate-200 pb-6">
          <div className="flex justify-between items-center">
            <Link href="/" className="inline-flex w-fit items-center rounded-2xl bg-white border border-slate-200 px-5 py-2.5 font-bold text-slate-600 hover:bg-slate-50 hover:text-slate-900 shadow-sm transition-all"><ArrowLeft className="w-4 h-4 mr-2" /> Home</Link>
            <Link href="/my-restaurant-orders" className="inline-flex w-fit items-center rounded-2xl bg-orange-50 text-orange-700 px-5 py-2.5 font-bold hover:bg-orange-100 transition-all">My Orders</Link>
          </div>
          <div>
            <h1 className="text-4xl font-black tracking-tight flex items-center gap-3"><Utensils className="w-10 h-10 text-orange-600" /> Find Food</h1>
            <p className="mt-2 text-slate-500 font-medium text-lg">Discover home cooks and restaurants near your location.</p>
          </div>
        </div>

        {/* LOCATION SETUP CONTAINER (Matches Ride Style) */}
        <div className="bg-white p-5 md:p-6 rounded-[2rem] shadow-xl shadow-slate-200/50 border border-slate-100 relative z-[1000] flex flex-col gap-6">
          <div className="relative z-50">
            <div className="flex justify-between items-end mb-1.5 px-1">
              <label className="text-xs font-black text-slate-900 uppercase tracking-widest">SEARCH LOCATION</label>
              <button onClick={getBrowserLocation} disabled={gettingLocation} className="text-xs font-bold text-orange-600 flex items-center hover:text-orange-800 transition-colors">
                {gettingLocation ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1"/> : <LocateFixed className="w-3.5 h-3.5 mr-1"/>} Use Current GPS
              </button>
            </div>
            
            <div className="flex items-center bg-white rounded-2xl p-1.5 border border-slate-200 focus-within:border-orange-400 focus-within:ring-2 focus-within:ring-orange-100 transition-all shadow-sm">
              <MapPin className="w-5 h-5 text-orange-500 ml-3 mr-2 shrink-0" />
              <div className="flex-1 min-w-0 px-2">
                <input
                  type="text"
                  value={manualLocation}
                  onChange={(e) => setManualLocation(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); searchManualLocation(); } }}
                  placeholder="Enter area, city or landmark..."
                  className="w-full bg-transparent text-sm md:text-base font-bold outline-none text-slate-900 placeholder:text-slate-400"
                />
              </div>
              {manualLocation && (
                <button onClick={() => setManualLocation("")} className="text-slate-400 hover:text-slate-600 p-1 mr-1">
                  <XCircle className="w-4 h-4"/>
                </button>
              )}
              <button
                type="button"
                onClick={searchManualLocation}
                disabled={searchingLocation}
                className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-2.5 rounded-xl font-black text-sm shadow-md transition-all active:scale-95"
              >
                {searchingLocation ? <Loader2 className="w-4 h-4 animate-spin"/> : "Search"}
              </button>
            </div>
            {gettingLocation && (
              <p className="text-xs font-semibold text-orange-500 ml-3 mt-2 animate-pulse flex items-center">
                <Loader2 className="w-3 h-3 animate-spin mr-1"/> Detecting your exact GPS location...
              </p>
            )}
          </div>
        </div>

        {/* SEARCH & FILTERS */}
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4">
          <div className="flex items-center bg-white rounded-2xl p-2 border border-slate-200 focus-within:border-orange-400 focus-within:ring-2 focus-within:ring-orange-100 transition-all shadow-sm">
            <Search className="w-5 h-5 text-slate-400 ml-3 mr-2 shrink-0" />
            <input
              type="text"
              placeholder="Search food name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 min-w-0 px-2 bg-transparent text-sm md:text-base font-bold outline-none text-slate-900 placeholder:text-slate-400"
            />
          </div>

          <select
            value={foodSellerFilter}
            onChange={(event) => setFoodSellerFilter(event.target.value as FoodSellerFilter)}
            className="w-full md:w-[180px] bg-white rounded-2xl px-4 py-3.5 border border-slate-200 font-black text-slate-700 outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 shadow-sm appearance-none"
          >
            <option value="All">All Types</option>
            <option value="HomeCook">Home Cooks</option>
            <option value="Restaurant">Restaurants</option>
          </select>
        </div>

        {/* DISTANCE FILTERS */}
        <div className="bg-white p-4 md:p-5 rounded-[2rem] shadow-sm border border-slate-200">
          <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-3">Filter by Distance</p>
          <div className="flex flex-wrap gap-2.5">
            {[2, 5, 25, 50].map((km) => (
              <button
                key={km}
                type="button"
                onClick={() => setDistanceFilter(km as DistanceFilter)}
                className={`px-5 py-2 rounded-xl text-sm font-black transition-all shadow-sm ${
                  distanceFilter === km
                    ? "bg-orange-600 text-white border-transparent"
                    : "bg-slate-50 text-slate-600 border border-slate-200 hover:bg-orange-50 hover:text-orange-700"
                }`}
              >
                Within {km} km
              </button>
            ))}
          </div>
        </div>

        {/* MAP */}
        {locationReady && !error && (
          <NearbyFoodMap
            foods={visibleFoods}
            restaurants={visibleRestaurants}
            buyerLatitude={searchLatitude}
            buyerLongitude={searchLongitude}
            radiusKm={distanceFilter}
            onLocationChange={updateSearchLocationFromMap}
          />
        )}

        {/* LOADING & ERRORS */}
        {loading && <div className="py-12 flex justify-center"><Loader2 className="w-10 h-10 animate-spin text-orange-500" /></div>}
        {error && <div className="py-12 text-center bg-rose-50 rounded-2xl border border-rose-200"><AlertCircle className="w-8 h-8 text-rose-500 mx-auto mb-2"/><p className="font-black text-rose-700">{error}</p></div>}

        {/* NO FOOD STATE */}
        {!loading && !error && visibleFoods.length === 0 && visibleRestaurants.length === 0 && (
          <div className="bg-white rounded-[2rem] border border-slate-200 p-12 text-center shadow-sm">
            <div className="text-6xl mb-4 opacity-50">🍽️</div>
            <h3 className="text-xl font-black text-slate-900">No food found near here</h3>
            <p className="text-sm font-semibold text-slate-500 mt-2">Try increasing the distance filter or search a different area.</p>
          </div>
        )}

        {/* RESULTS GRID */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {visibleFoods.map((food) => (
            <Link
              key={food.id}
              href={`/food/${food.id}`}
              className="group bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-xl hover:shadow-orange-200/50 hover:border-orange-200 transition-all p-5 flex items-start gap-4"
            >
              {food.image ? (
                <img src={food.image} alt={food.foodName} className="w-20 h-20 rounded-xl object-cover border border-slate-100 shrink-0 shadow-sm" />
              ) : (
                <div className="w-20 h-20 rounded-xl bg-orange-50 text-orange-500 flex items-center justify-center text-3xl border border-orange-100 shrink-0 shadow-sm">
                  🍱
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-xs font-black text-slate-500 uppercase tracking-wide truncate">
                  👨‍🍳 {food.seller?.foodSellerName || food.seller?.name || "Home Cook"}
                </p>
                <h2 className="mt-1 text-lg font-black text-slate-900 group-hover:text-orange-600 transition-colors truncate">
                  {food.foodName}
                </h2>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span className="bg-green-50 text-green-700 px-2.5 py-1 rounded-lg text-xs font-black border border-green-200">₹{food.price}</span>
                  <span className="bg-slate-100 text-slate-600 px-2.5 py-1 rounded-lg text-xs font-bold border border-slate-200">📦 {food.quantity} left</span>
                  {food.distanceKm != null && (
                    <span className="bg-orange-50 text-orange-700 px-2.5 py-1 rounded-lg text-xs font-bold border border-orange-200">📍 {Number(food.distanceKm).toFixed(1)} km</span>
                  )}
                </div>
              </div>
            </Link>
          ))}

          {visibleRestaurants.map((restaurant) => {
            const restaurantName = restaurant.seller?.foodSellerName || "Restaurant";
            return (
              <Link
                key={`restaurant-${restaurant.sellerId}`}
                href={`/restaurant/${restaurant.sellerId}`}
                className="group bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-xl hover:shadow-orange-200/50 hover:border-orange-200 transition-all p-5 flex items-start gap-4"
              >
                <div className="w-20 h-20 rounded-xl bg-slate-50 text-slate-500 flex items-center justify-center text-3xl border border-slate-100 shrink-0 shadow-sm">
                  <Store className="w-8 h-8 text-slate-400" />
                </div>
                <div className="min-w-0 flex-1 self-center">
                  <p className="text-xs font-black text-slate-400 uppercase tracking-wide">Restaurant Menu</p>
                  <h2 className="mt-1 text-lg font-black text-slate-900 group-hover:text-orange-600 transition-colors truncate">
                    🍽️ {restaurantName}
                  </h2>
                  {restaurant.distanceKm != null && (
                    <p className="mt-2 inline-block bg-orange-50 text-orange-700 px-2.5 py-1 rounded-lg text-xs font-bold border border-orange-200">
                      📍 {Number(restaurant.distanceKm).toFixed(1)} km away
                    </p>
                  )}
                </div>
              </Link>
            );
          })}
        </div>

      </div>
    </main>
  );
}